import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import IconContentAdd from '@material-ui/icons/Add'
import IconCancel from '@material-ui/icons/Cancel'
import get from 'lodash/get'
import set from 'lodash/set'
import { useState } from 'react'
import { Button as RaButton, FormWithRedirect, SaveButton, useNotify, useRefresh } from 'react-admin'
import { scrollToErrorsInDialog } from 'shared/hooks/useScrollToErrors'

import { captureExceptionIfProduction, getEquipmentResourceName } from 'helpers/utils'
import { addEquipment, createDocument, updateDocument, updateEquipment } from '../../api/core'
import { EquipmentType } from '../../helpers/constants'
import {
  TRAILER,
  TRAILER_LAST_DOT_INSPECTION,
  TRUCK,
  TRUCK_LAST_DOT_INSPECTION,
} from '../../helpers/documents'
import { withDisableBackdropClick } from '../../helpers/withDisableBackdropClick'
import { useHasSyncTruckToMotive } from '../../hooks/useHasFeatureFlag'
import { TrailerForm, TruckForm } from './EquipmentForm'

const sanitizeEmptyDateValues = (values: any) => {
  const fields = ['date_last_dot_inspection', 'date_acquired']
  for (let field of fields) {
    if (!get(values, field)) {
      set(values, field, null)
    }
  }
}

type EquipmentQuickCreateUpdateButtonProps = {
  record: any
  equipmentType: string
  isCreate: boolean
}

export function EquipmentQuickCreateUpdateButton({
  record,
  equipmentType,
  isCreate,
}: EquipmentQuickCreateUpdateButtonProps) {
  const [showDialog, setShowDialog] = useState(false)
  const [loading, setLoading] = useState(false)
  const notify = useNotify()
  const refresh = useRefresh()
  const teamId = isCreate ? record.id : record.team
  const hasTruckSyncToMotive = useHasSyncTruckToMotive()

  const EquipmentForm = ({ initialValues }: any) =>
    equipmentType === EquipmentType.Truck ? (
      <TruckForm
        initialValues={initialValues}
        teamId={teamId}
        isCreate={isCreate}
        hasTruckSyncToMotive={hasTruckSyncToMotive}
      />
    ) : (
      <TrailerForm initialValues={initialValues} isCreate={isCreate} />
    )

  const equipmentString = equipmentType === EquipmentType.Truck ? 'Truck' : 'Trailer'
  const actionString = [isCreate ? 'Add' : 'Edit', equipmentString].join(' ')
  const resourceName = getEquipmentResourceName(equipmentType)

  const handleClick = () => setShowDialog(true)

  const handleClose = () => setShowDialog(false)

  const handleSubmit = async (values: any) => {
    setLoading(true)
    sanitizeEmptyDateValues(values)
    if (values.insurance_period_date_start) {
      values.insurance_period_kwargs = {
        insurance_period_date_start: values.insurance_period_date_start,
        ...(values.insurance_period_date_end && {
          insurance_period_date_end: values.insurance_period_date_end,
        }),
        ...(values.insurance_period_date_end_reason && {
          insurance_period_date_end_reason: values.insurance_period_date_end_reason,
        }),
      }
    }

    values.equipment_type = equipmentType

    const makeDocumentRequest = Boolean(values.date_last_dot_inspection)
    const documentRequestPayload = {
      folder_name: equipmentType === EquipmentType.Truck ? TRUCK : TRAILER,
      document_type:
        equipmentType === EquipmentType.Truck ? TRUCK_LAST_DOT_INSPECTION : TRAILER_LAST_DOT_INSPECTION,
      metadata: {
        LAST_DOT_INSPECTION_DATE: values.date_last_dot_inspection,
      },
      LAST_DOT_INSPECTION: null,
      related_object_pk: null,
    }

    values.trailer_type = values.trailer_type?.toUpperCase()

    delete values.date_last_dot_inspection

    let equipmentRequest = isCreate
      ? addEquipment(record.id, values)
      : updateEquipment(record.id, equipmentType.toLowerCase(), values)

    equipmentRequest
      .then((data) => {
        if (makeDocumentRequest) {
          documentRequestPayload['related_object_pk'] = data.id
          if (isCreate) {
            return createDocument('', documentRequestPayload)
          } else {
            return updateDocument(data?.last_dot_inspection_document?.id, documentRequestPayload)
          }
        }
      })
      .then(() => {
        let successMessage = `${equipmentString} updated successfully.`
        if (hasTruckSyncToMotive && equipmentType === EquipmentType.Truck) {
          successMessage += 'A record of this truck is also created in Motive with the Motive ID shown above.'
        }
        notify(successMessage, { type: 'success' })
        refresh()
        setShowDialog(false)
      })
      .catch((error) => {
        const error_data = error.response.data
        notify(JSON.stringify(error_data), { type: 'error' })
        const status = get(error, 'response.status')
        const vinErrorMessage = get(error_data, 'vin_number', '')
        const eld_serial_number = get(error_data, 'eld_serial_number', '')
        if (
          status === 400 &&
          (vinErrorMessage.includes('Equipment with this VIN already exists') ||
            vinErrorMessage.includes('VIN number cannot contain I, O, or Q.') ||
            eld_serial_number.includes('Equipment with this VIN already exists') ||
            eld_serial_number.includes('Already used on another non-removed truck') ||
            eld_serial_number.includes('This team has other truck(s) with the same ELD serial number') ||
            eld_serial_number.includes('Keeptruckin failed') ||
            eld_serial_number.includes('Failed to fetch vehicle info') ||
            eld_serial_number.includes('Cannot assign ELD') ||
            eld_serial_number.includes('ELD device not found'))
        )
          captureExceptionIfProduction(error)
      })
      .finally(() => {
        setLoading(false)
      })
  }

  let initialValues: any = null
  if (!isCreate) {
    initialValues =
      equipmentType === EquipmentType.Truck
        ? {
            ...record,
            assigned_user_ids: record.drivers.map((value: any) => value.id),
          }
        : record
  }

  return (
    <>
      <RaButton onClick={handleClick} label={actionString}>
        <IconContentAdd />
      </RaButton>
      <Dialog
        fullWidth
        open={showDialog}
        onClose={withDisableBackdropClick(handleClose)}
        aria-label={actionString}
      >
        <DialogTitle>{actionString}</DialogTitle>
        <FormWithRedirect
          initialValues={initialValues}
          resource={resourceName}
          save={handleSubmit}
          render={(props) => {
            const { handleSubmitWithRedirect } = props
            const handleSubmit = (...args: any[]) => {
              const { errors, invalid } = props.form.getState()
              if (handleSubmitWithRedirect) handleSubmitWithRedirect(...args)
              if (invalid && errors) {
                scrollToErrorsInDialog(errors, '.MuiFormControl-root, .MuiFormGroup-root')
              }
            }

            return (
              <>
                <DialogContent>
                  <EquipmentForm initialValues={initialValues} />
                </DialogContent>
                <DialogActions>
                  <Button onClick={handleClose} disabled={loading}>
                    <IconCancel />
                  </Button>
                  <SaveButton handleSubmitWithRedirect={handleSubmit} disabled={loading} />
                </DialogActions>
              </>
            )
          }}
        />
      </Dialog>
    </>
  )
}
